import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { HttpClient } from '@angular/common/http';
import { Client } from '@app/shared';

@Injectable()
export class MapService {
  public map: L.Map;
  public baseMaps: any;
  private polylines = [];
  private markers = [];
  private cabMarker;
  private cabMarkers = [];
  currentCab;

  followDriver = false;
  showAllDrivers = false;

  constructor(private http: HttpClient, private client: Client) {
    const osmAttr =
      '&copy; <a href=\'http://www.openstreetmap.org/copyright\'>OpenStreetMap</a>, ' +
      'Tiles courtesy of <a href=\'http://hot.openstreetmap.org/\' target=\'_blank\'>Humanitarian OpenStreetMap Team</a>';

    const esriAttr =
      'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, ' +
      'iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, ' +
      'Esri China (Hong Kong), and the GIS User Community';

    const cartoAttr =
      '&copy; <a href=\'http://www.openstreetmap.org/copyright\'>OpenStreetMap</a> ' +
      '&copy; <a href=\'http://cartodb.com/attributions\'>CartoDB</a>';

    const UtaxAttr = 'UTAX';

    this.baseMaps = {
      OpenStreetMap: L.tileLayer(
        'http://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        {
          attribution: osmAttr
        }
      ),
      Esri: L.tileLayer(
        'http://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: esriAttr
        }
      ),
      CartoDB: L.tileLayer(
        'http://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
        {
          attribution: cartoAttr
        }
      ),
      Utax: L.tileLayer(this.client.getTileUrl(), {
        attribution: UtaxAttr
      })
    };
  }

  disableMouseEvent(elementId: string) {
    const element = <HTMLElement>document.getElementById(elementId);

    L.DomEvent.disableClickPropagation(element);
    L.DomEvent.disableScrollPropagation(element);
  }

  removeOrderPins() {
    this.markers.map(marker => this.map.removeLayer(marker));
    this.markers = [];
  }

  addOrderPin(x, y, num) {
    let orderLocation = new L.LatLng(x, y);
    let icon = L.divIcon({
      className: '',
      iconSize: null,
      html: `<div class="map-marker">
      <div class="caption">${num}</div>
      </div>`
    });
    const marker = L.marker(orderLocation, { icon: icon }).addTo(this.map);
    this.markers.push(marker);
    return this.markers;
  }

  addCabPin(car, active) {
    //console.log('TCL: MapService -> addCabPin -> active', active);
    let location = new L.LatLng(car.location.lat, car.location.lng);

    const popup = `<div>
    <p>id : ${car.id} </p>
    <p>status : ${car.is_active}</p>
    <p>create time: ${car.callsign}</p>
 <div>`;
    let icon = L.divIcon({
      className: '',
      iconSize: null,
      html: `<div class="car-marker">
      <div class="${active ? 'car-caption' : 'inactive-car-caption'}">${
        car.callsign
      }</div>
      </div>`
    });
    const marker = L.marker(location, { icon: icon })
      .bindPopup(popup)
      .addTo(this.map);
    if (active && this.followDriver) { this.fitBounds([marker]); }
    return marker;
  }

  addCabs(cabs, fitBounds = false) {
    cabs.map(car => {
      let isCurrent = !this.currentCab || car.id == this.currentCab.id;
      let marker;

      if (this.showAllDrivers || isCurrent) {
        marker = this.addCabPin(car, this.currentCab && isCurrent);
        this.cabMarkers.push({
          id: car.id,
          marker: marker
        });
      }

      if (isCurrent) { this.cabMarker = marker; }
    });

    if (this.map && !!cabs[0] && fitBounds) {
      this.fitBounds(this.cabMarkers.map(el => el.marker));
    }
  }

  fitBounds(args: Array<any>) {
    if (!args[0]) { return; }
    const group = L.featureGroup(args);
    try {
      this.map.invalidateSize();
      this.map.fitBounds(group.getBounds());
    } catch (ex) {
      console.log(ex);
    }
  }

  removeCabs(cabs) {
    cabs.map(cab => {
      let removedEl = this.cabMarkers.find(el => el.id == cab.id);
      if (!removedEl) { return; }
      this.map.removeLayer(removedEl.marker);
      this.cabMarkers = this.cabMarkers.filter(el => el != cab.id);
    });
  }

  removeAllCabs() {
    this.cabMarkers.map(el => this.map.removeLayer(el.marker));
    this.cabMarkers = [];
  }

  moveCabs(cabs) {
    cabs.map(cab => {
      let pin = this.cabMarkers.find(el => el.id == cab.id);
      if (pin) {
        pin.marker.setLatLng(new L.LatLng(cab.location.lat, cab.location.lng));
      }
    });
  }

  addCab(car) {
    if (this.cabMarker) { this.map.removeLayer(this.cabMarker); }
    if (!car) { return; }
    this.cabMarker = this.addCabPin(car, true);
    this.cabMarker.cabId = car.id;
    return this.cabMarker;
  }

  focusOnCab() {
    this.fitBounds([this.cabMarker]);
  }

  localtionCab(car, zoom?: boolean) {
    if (zoom) {
      this.map.setView(L.latLng(car.location.lat, car.location.lng), 14);
    }

    this.map.panTo(L.latLng(car.location.lat, car.location.lng), {
      animate: true
    });
  }

  decode(encoded) {
    const points = [];
    let index = 0,
      len = encoded.length;
    let lat = 0,
      lng = 0;
    while (index < len) {
      let b,
        shift = 0,
        result = 0;
      do {
        b = encoded.charAt(index++).charCodeAt(0) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      let dlat = (result & 1) != 0 ? ~(result >> 1) : result >> 1;
      lat += dlat;
      shift = 0;
      result = 0;
      do {
        b = encoded.charAt(index++).charCodeAt(0) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      let dlng = (result & 1) != 0 ? ~(result >> 1) : result >> 1;
      lng += dlng;

      points.push([lat / 1e5, lng / 1e5]);
    }
    return points;
  }


  drawPoly(path, options = {}, zoom = true) {
    if(!path) return;
    let poly = new L.Polyline(this.decode(path), options).addTo(this.map);
    if(zoom){
      this.map.invalidateSize();
      this.map.fitBounds(poly.getBounds());
    }
    this.polylines.push(poly);
  }

  removePoly() {
    if (this.map) { this.polylines.map(poly => this.map.removeLayer(poly)); }
    this.polylines = [];
    // let el = document.getElementsByClassName('leaflet-interactive')[0];
    // if (el) el.remove();  //fix in leaflet!
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/map.service.ts